import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import { get, post, put, del } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

// Assessment Templates API Types
export interface CreatedByUser {
  id: string;
  name: string;
  email: string;
}

export interface AssessmentTemplate {
  id: string;
  name: string;
  description: string;
  categories: string[];
  questionnaire_type: "small" | "large";
  questions_count: number;
  created_at: string;
  updated_at: string;
  created_by: CreatedByUser;
}

export interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_items: number;
  total_pages: number;
  has_next: boolean;
  has_prev: boolean;
}

export interface TemplateListData {
  templates: AssessmentTemplate[];
  pagination: PaginationInfo;
}

export interface TemplateListResponse {
  success: boolean;
  data: TemplateListData;
}

export interface QuestionOption {
  value: string;
  label: string;
}

export interface Question {
  id?: string;
  question: string;
  questionType: any;
  required: boolean;
  options?: QuestionOption[] | null;
  display_order?: number;
}

export interface CreateTemplateRequest {
  name: string;
  description?: string;
  categories: string[];
  questionnaire_type: "short" | "long" | string;
  questions: Question[];
}

export interface Assessment {
  id: string;
  name: string;
  description?: string;
  template_id: string;
  template_name: string;
  status:
    | "active"
    | "completed"
    | "draft"
    | "upcoming"
    | "aborted"
    | "published";
  progress: number;
  participants: number;
  total_responses: number;
  total_questions: number;
  due_on: string;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
  hub_publish_scope?: "partial" | "full" | null;
  created_by: CreatedByUser;
  target_accounts?: Array<{
    account_id: string;
    account_name: string;
    account_code?: string;
  }>;
  categories?: string[];
}

export interface AssessmentListData {
  assessments: Assessment[];
}

export interface AssessmentListResponse {
  success: boolean;
  data: AssessmentListData;
}

// Backend API Response Format
export interface BackendAssessmentResponse {
  assessments: any;
  total: number;
  page: number;
  page_size: number;
}

export interface BackendAssessment {
  assessment_id: string;
  template_id: string | null;
  name: string | null;
  title: string;
  description: string | null;
  questions: number | null;
  due_on: string | null;
  due_date: string;
  start_date: string;
  status: string; // "ACTIVE", "COMPLETED", "DRAFT", "UPCOMING"
  category: string | null;
  target_level: string | null;
  target_tier: string | null;
  target_industry: string | null;
  frequency: string;
  is_active: boolean;
  allow_edit_after_submit: boolean;
  target_accounts: any | null;
  target_questionnaires: string | null;
  send_at: string | null;
  send_now: boolean | null;
  reminder: string | null;
  tenant_id: string;
  created_by: string;
  created_at: string;
  updated_at: string;
  template: {
    name?: string;
    id?: string;
  } | null;
}

export type AssessmentHubStatus =
  | "pending"
  | "active"
  | "completed"
  | "published";

export interface AssessmentHubExecutive {
  id: string;
  name: string;
  email?: string;
  role: "Primary Executive Sponsor" | "Secondary Executive Sponsor";
  status: "pending" | "in_progress" | "completed";
}

export interface AssessmentHubAccount {
  id?: string;
  name?: string;
  account_id: string;
  account_name: string;
  totalAssigned: number;
  responses: number;
  minLevel: string | null;
  medianLevel: string | null;
  maxLevel: string | null;
  finalLevel: string | null;
  overrideLevel?: boolean;
  hubPublished?: boolean;
  published_at?: string | null;
  published_level?: string | null;
  executives?: AssessmentHubExecutive[];
}

interface AssessmentHubApiAssessment {
  assessment_id: string;
  name: string;
  description?: string | null;
  template_id: string;
  template_name: string;
  status: string;
  progress: number;
  participants: number;
  total_responses: number;
  total_questions: number;
  category?: "short" | "long" | null;
  due_on?: string;
  created_at: string;
  updated_at: string;
  published_at?: string | null;
  created_by: CreatedByUser;
  accounts?: AssessmentHubAccount[];
  target_questionnaires?: string;
}

export interface AssessmentHubData extends AssessmentHubApiAssessment {
  id: string;
  status: "draft" | "upcoming" | "active" | "completed" | "published";
  dueDate?: string;
  createdBy?: string;
}

const mapStatusToHubKey = (status: string): AssessmentHubStatus => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "active") return "active";
  if (normalized === "completed") return "completed";
  if (normalized === "published") return "published";
  return "pending";
};

export const HUB_STATUS_KEYS: AssessmentHubStatus[] = [
  "pending",
  "active",
  "completed",
  "published",
];

export interface AssessmentHubPagination {
  page: number;
  page_size: number;
  total: number;
}
export interface CreateAssessmentRequest {
  name: string;
  description?: string;
  accounts: Array<{
    account_id: string;
    executive_ids: string[];
  }>;
  data: {
    metadata?: {
      categories?: string[];
    };
    questions: any[];
  };
  target_questionnaires: string;
  status: "draft" | "active" | "upcoming";
  due_on: string;
  reminder_frequency?: string;
  send_now: boolean;
}

interface FetchTemplatesParams {
  page?: number;
  limit?: number;
  search?: string;
  questionnaire_type?: "small" | "large";
  category?: string;
  sort_by?: "name" | "created_at" | "updated_at";
  sort_order?: "asc" | "desc";
}

interface FetchAssessmentsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?:
    | "active"
    | "completed"
    | "draft"
    | "upcoming"
    | "published"
    | "all-status";
  template_id?: string;
  sort_by?: "name" | "created_at" | "due_on" | "status";
  sort_order?: "asc" | "desc";
}

export interface UpdateAssessmentPayload {
  name?: string;
  description?: string;
  due_on?: string;
  reason?: string;
  questions?: any;
  target_accounts?: Array<{
    account_id: string;
    account_name: string;
  }>;
  target_questionnaires?: string;
  reminder_frequency?: string;
  send_now?: boolean;
  send_at?: string;
}

export interface UpdateAssessmentStatusPayload {
  status: "active" | "completed" | "published";
}

// Review Assessments API Types
export interface ReviewAssessmentExecutive {
  executive_id: string;
  executive_name: string;
  status:
    | "pending"
    | "in_progress"
    | "draft"
    | "completed"
    | "published"
    | "aborted";
  completed_on: string | null;
  disconnect_status?: string | null;
  publishing?: string | null;
  /** Assignment last update — used for “submitted X ago” on Re-assess. */
  updated_at?: string | null;
  submitted_at?: string | null;
  notes?: {
    origin?: string | null;
    carry_forward?: boolean | null;
    carried_in_at_publish?: boolean | null;
    stale_at_publish?: boolean | null;
    excluded?: {
      reason?: string | null;
      submitted_on?: string | null;
      days_since?: number | null;
    } | null;
  } | null;
  response: Record<string, string | string[]> | null;
}

export interface ReviewAssessmentAccount {
  account_id: string;
  account_name: string;
  account_tier: string;
  executives: ReviewAssessmentExecutive[];
}

export interface ReviewAssessment {
  assessment_id: string;
  abort_reason: string | null;
  assessment_name: string;
  description: string | null;
  assessment_type?: string | null;
  notes?: {
    source?: string | null;
    converted_from?: string | null;
    self_assessment_id?: string | null;
    formal_responses_locked?: boolean;
  } | null;
  category: "short" | "long";
  status:
    | "pending"
    | "in_progress"
    | "draft"
    | "completed"
    | "published"
    | "aborted";
  due_date: string;
  created_at: string;
  created_by: string;
  accounts: ReviewAssessmentAccount[];
  questions: any;
  /** Team list: true when this account already has an open Formal. */
  account_has_open_formal?: boolean;
  /** Team list: true when this Team cycle was superseded by a Formal launch. */
  superseded_by_formal?: boolean;
}

export interface ReviewAssessmentsResponse {
  executive_id: string;
  tab: "assigned" | "draft" | "completed" | "published" | "aborted";
  filters: {
    account_id: string;
    search: string;
  };
  assessments: ReviewAssessment[];
  meta: {
    total_count: number;
  };
}

export interface FetchReviewAssessmentsParams {
  executive_id: string;
  tab?: "assigned" | "draft" | "completed" | "published" | "aborted";
  account_id?: string;
  search?: string;
  is_draft?: boolean;
}

export interface SubmitReviewAssessmentPayload {
  assessment_id: string;
  account_id: string;
  responses: Record<string, string | string[]>;
  is_draft: boolean;
  carry_forward?: boolean;
  prior_response_id?: string;
  carry_forward_note?: string;
}

export type InputDisposition =
  | "counted"
  | "counted_both"
  | "updated_both"
  | "private_no_round"
  | "held_next_cycle"
  | "held_until_release_formal"
  | "held_until_release_team"
  | "held_misses_formal"
  | "included_formal"
  | "formal_closed_private";

export interface SubmitReviewAssessmentResponse {
  success?: boolean;
  message?: string;
  data?: any;
  assessment_id?: string;
  previous_assessment_id?: string | null;
  replaced_stale_assessment?: boolean;
  account_id?: string | null;
  assignment_id?: string;
  status?: string;
  assessment_status?: string | null;
  saved_at?: string;
  submitted_at?: string | null;
  all_completed?: boolean;
  /** Set when the input was kept as a private remainder input (ISO date). */
  remainder_counts_on?: string | null;
  /** Where the submitted input ended up (drives the success popup text). */
  input_disposition?: InputDisposition | null;
  input_held?: boolean;
  account_name?: string | null;
  /** Due date of the round the input relates to (ISO date). */
  round_due_date?: string | null;
}

export interface CreateSelfAssessmentPayload {
  account_id: string;
  name?: string;
  description?: string;
  as_draft?: boolean;
  data: {
    questions: unknown[];
  };
}

export interface PriorSelfAssessmentResponse {
  response_id?: string;
  assignment_id?: string;
  account_id?: string;
  responses?: {
    rationale?: string;
    maturity_level?: string;
    [key: string]: string | undefined;
  };
  submitted_at?: string | null;
  updated_at?: string | null;
  is_draft?: boolean;
}

export interface PriorSelfAssessmentMeta {
  assessment_id?: string;
  title?: string;
  status?: string;
  assessment_type?: string;
  due_date?: string | null;
  assessment_remaining_date?: string | null;
  created_at?: string | null;
  is_stale?: boolean;
  disconnect_status?: string | null;
  assignment_status?: string | null;
  /** "self" (team) | "formal" — which cycle type the prior belongs to */
  origin?: "self" | "formal" | string | null;
}

export interface ActiveDisconnectInfo {
  status?: string | null;
  assessment_id?: string | null;
  resolution_id?: string | null;
}

export interface LatestSelfAssessmentCycleDraft {
  assessment_id?: string;
  response?: PriorSelfAssessmentResponse | null;
}

export interface LatestSelfAssessmentContent {
  account_id: string;
  user_id?: string;
  found?: boolean;
  has_response?: boolean;
  /** "self" (team) | "formal" — prior completed response origin */
  origin?: "self" | "formal" | string | null;
  self_assessment?: PriorSelfAssessmentMeta | null;
  response?: PriorSelfAssessmentResponse | null;
  /** Open Team cycle draft (separate from completed prior). */
  cycle_draft?: LatestSelfAssessmentCycleDraft | null;
  active_disconnect?: ActiveDisconnectInfo | null;
  /** Completed prior is in its own cycle or the next (Carry forward allowed). */
  input_current?: boolean;
  /** Days since the completed prior was submitted (server date). */
  input_days_since?: number | null;
  /** Prior is older than the cycle length: label shows Stale. */
  input_label_stale?: boolean;
}

export interface DisconnectSponsorAssessment {
  executiveId: string;
  name: string;
  title?: string | null;
  role?: string | null;
  /** Null when the assignee has not submitted a maturity level. */
  level: number | null;
  levelLabel: string | null;
  rationale?: string | null;
  submittedAt?: string | null;
  assignmentId?: string | null;
}

export interface DisconnectResolutionDetail {
  id: string;
  status: string;
  due_date?: string | null;
  context_note?: string | null;
  agreed_level?: number | null;
  resolution_justification?: string | null;
  triggered_at?: string | null;
  resolved_at?: string | null;
  published_at?: string | null;
  triggeredByName?: string | null;
  resolvedByName?: string | null;
  publishedByName?: string | null;
}

export interface DisconnectResolution extends DisconnectResolutionDetail {
  assessment_id: string;
  account_id: string;
  triggered_by?: string | null;
  delta_tier: string;
  delta_value: number;
  sponsor_assessments: DisconnectSponsorAssessment[];
  min_level?: number | null;
  max_level?: number | null;
  median_level?: number | null;
  esl_final_level?: number | null;
  esl_justification?: string | null;
  resolved_by?: string | null;
  published_by?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  accountName?: string | null;
  assessmentName?: string | null;
  assessmentType?: string | null;
  primarySponsorId?: string | null;
  hubPublished?: boolean;
  /** One disconnect shared by a Formal and Team published together. */
  combined?: boolean;
}

export interface DisconnectResolutionsResponse {
  resolutions: DisconnectResolution[];
  total: number;
}

export interface FetchDisconnectResolutionsParams {
  status?: "pending_resolution" | "resolved" | "published";
  assessmentId?: string;
  accountId?: string;
  accountName?: string;
}

export interface RecordResolutionPayload {
  resolutionId: string;
  responseType: "keep_original" | "agree_with_esl" | "override_assessment";
  agreedLevel?: number;
  justification: string;
}

export interface PublishAssessmentAccountsPayload {
  assessmentId: string;
  accountIds?: string[];
  appendFromOngoing?: boolean;
  force?: boolean;
}

export interface PublishPostResolutionPayload {
  resolutionId: string;
}

export interface DisconnectReviewAccount {
  accountId: string;
  accountName: string;
  accountSector?: string | null;
  assessmentId: string;
  assessmentTitle: string;
  assessmentType?: string | null;
  /** team | formal — from API origin (self type or publish/convert notes). */
  assessmentOrigin?: "team" | "formal" | null;
  assessmentStatus: string;
  assessmentDescription?: string | null;
  assessmentDueDate?: string | null;
  assessmentCreatedAt?: string | null;
  assessmentPublishedAt?: string | null;
  assessmentCompletedAt?: string | null;
  createdByName?: string | null;
  delta: number;
  deltaTier: string;
  minLevel: number;
  maxLevel: number;
  medianLevel: number;
  eslFinalLevel: number;
  eslJustification?: string | null;
  isEslOverridden: boolean;
  sponsorAssessments: DisconnectSponsorAssessment[];
  disconnectStatus: string;
  resolutionId?: string | null;
  triggeredByName?: string | null;
  resolvedByName?: string | null;
  publishedByName?: string | null;
  hubPublished?: boolean;
  combined?: boolean;
  resolution?: DisconnectResolutionDetail | null;
  /** Hub publish stamp: manual_force_publish | cron_auto_publish, etc. */
  publishMode?: string | null;
}

export interface DisconnectKpiCounts {
  strong_disconnect: number;
  soft_disconnect: number;
  not_triggered: number;
  ready_to_publish: number;
}

export interface DisconnectReviewResponse {
  accounts: DisconnectReviewAccount[];
  counts?: {
    ongoing?: DisconnectKpiCounts;
    logs?: DisconnectKpiCounts;
  };
  threshold: number;
  defaultDueDateDays: number;
  formalDefaultDueDateDays?: number;
  teamDefaultDueDateDays?: number;
  reminderDaysBefore: number;
  levelGapThreshold: string;
}

export interface FetchDisconnectReviewParams {
  assessmentId?: string;
  assessmentStatus?: "completed" | "published" | "all";
}

export interface TriggerDisconnectAccountPayload {
  accountId: string;
  assessmentId: string;
  accountName?: string;
  deltaTier: string;
  delta: number;
  minLevel: number;
  maxLevel: number;
  medianLevel: number;
  eslFinalLevel: number;
  eslJustification?: string | null;
  sponsorAssessments: DisconnectSponsorAssessment[];
}

export interface TriggerDisconnectPayload {
  dueDate?: string;
  contextNote?: string;
  accounts: TriggerDisconnectAccountPayload[];
}

export interface TriggerDisconnectResponse {
  message: string;
  resolutions: Record<string, unknown>[];
}

export interface SelfAssessmentConfig {
  id: string;
  /** Deprecated: currency now follows cadence_days */
  days: number;
  /** Deprecated: currency now follows cadence_days */
  account_days?: number;
  formal_due_reminder_days?: number;
  team_due_reminder_days?: number;
  /** Calendar cycle length (60 or 90); Team windows open at each period start */
  cadence_days?: number;
  window_days?: number;
  /** Fixed offset from the period start; null = first business day */
  window_offset_days?: number | null;
  /** Primary Sponsor disconnect resolution window */
  resolution_days?: number;
  /** Extra days after the cycle length that a publish still counts an input */
  grace_extra_days?: number;
  created_at?: string;
  updated_at?: string;
}

export interface UpdateSelfAssessmentConfigPayload {
  id: string;
  days?: number;
  account_days?: number;
  formal_due_reminder_days?: number;
  team_due_reminder_days?: number;
  cadence_days?: number;
  window_days?: number;
  window_offset_days?: number | null;
  resolution_days?: number;
  grace_extra_days?: number;
}

export interface FetchSelfAssessmentsParams {
  status?: string;
  account_id?: string;
  search?: string;
  created_by_me?: boolean;
  page?: number;
  page_size?: number;
}

export interface FetchVendorSelfAssessmentsParams {
  status?: string;
  search?: string;
  sort_by?: string;
  sort_order?: string;
  page?: number;
  page_size?: number;
}

// Questions and Responses API Types
export interface AssessmentQuestion {
  id: string;
  question_text: string;
  question_type: string;
  required: boolean;
  order_index: number;
  options?: {
    choices?: Array<{
      label: string;
      value: string;
    }>;
    allow_multiple?: boolean;
    max_length?: number;
    min_length?: number;
  };
}

export interface AssessmentResponse {
  id: string;
  assignment_id: string;
  executive_id: string;
  account_id: string;
  responses: Record<string, string>;
  completion_time?: number;
  submitted_at: string;
  is_draft: boolean;
  created_at: string;
  updated_at: string;
}

export interface AssessmentQuestionsAndResponses {
  assessment_id: string;
  title: string;
  description?: string | null;
  questions: {
    metadata?: {
      categories?: string[];
    };
    questions: AssessmentQuestion[];
  };
  responses: AssessmentResponse[];
  total_responses: number;
}

// Level Mix API Types
export type LevelMixScope = "formal" | "team" | "my";

export type LevelMixOrigin =
  | "FORMAL"
  | "SELF"
  | "DISCONNECT_VIA_FORMAL"
  | "DISCONNECT_VIA_SELF";

export type PublishedColumnStatus =
  | "published"
  | "awaiting_publish"
  | "ongoing";

export interface LevelMixAccount {
  id: string;
  name: string;
  publishedAt?: string | null;
  assessmentId?: string | null;
  assessmentName: string;
  isCurrentLevel: boolean;
  assessmentOrigin?: LevelMixOrigin;
  youLevel?: string | null;
  publishedLevel?: string | null;
  publishedStatus?: PublishedColumnStatus;
  youSubmittedAt?: string | null;
  orgPublishedAt?: string | null;
}

export interface LevelMixLevel {
  level: string;
  levelName: string;
  percentage: number | null;
  accounts: LevelMixAccount[];
  count: number;
  entryCount: number;
}

export interface LevelMixResponse {
  mixScope?: LevelMixScope;
  levels: LevelMixLevel[];
  totalAssessments: number;
  totalAccounts: number;
  totalTargeted?: number;
  sponsorCount?: number;
  incompleteAccounts?: any[];
  unassessedAccounts?: any[];
  unassessedCount?: number;
  recentAssessments?: Array<{
    id?: string;
    assessmentId?: string;
    title?: string;
    publishedAt?: string;
  }>;
}

export interface FetchLevelMixParams {
  startDate?: string;
  endDate?: string;
  assessmentId?: string;
  executiveId?: string;
  scope?: LevelMixScope;
}

interface AssessmentState {
  templates: any;
  selectedTemplate: AssessmentTemplate | null;
  assessments: Assessment[];
  assessmentPagination: PaginationInfo | null;
  isLoading: boolean;
  isCreatingTemplate: boolean;
  isUpdatingTemplate: boolean;
  isCreatingAssessment: boolean;
  isUpdatingAssessment: boolean;
  isDeletingAssessment: boolean;
  isLoadingAssessments: boolean;
  error: string | null;
  createTemplateSuccess: boolean;
  createAssessmentSuccess: boolean;
  hubAssessmentsByStatus: Record<AssessmentHubStatus, AssessmentHubData[]>;
  hubPaginationByStatus: Record<
    AssessmentHubStatus,
    AssessmentHubPagination | null
  >;
  hubLoadingByStatus: Record<AssessmentHubStatus, boolean>;
  hubErrorByStatus: Record<AssessmentHubStatus, string | null>;
  hubTotalsByStatus: Record<AssessmentHubStatus, number>;
  reviewAssessments: ReviewAssessment[];
  reviewAssessmentsLoading: boolean;
  reviewAssessmentsError: string | null;
  reviewAssessmentsMeta: { total_count: number } | null;
  isSubmittingReviewAssessment: boolean;
  submitReviewAssessmentError: string | null;
  submitReviewAssessmentSuccess: boolean;
  isCreatingSelfAssessment: boolean;
  createSelfAssessmentError: string | null;
  createdSelfAssessment: Record<string, unknown> | null;
  vendorSelfAssessments: any[];
  vendorSelfAssessmentsTotal: number;
  isLoadingVendorSelfAssessments: boolean;
  vendorSelfAssessmentsError: string | null;
  selfAssessmentLatest: LatestSelfAssessmentContent | null;
  isLoadingSelfAssessmentLatest: boolean;
  selfAssessmentLatestError: string | null;
  selfAssessmentConfig: SelfAssessmentConfig | null;
  isLoadingSelfAssessmentConfig: boolean;
  selfAssessmentConfigError: string | null;
  isUpdatingSelfAssessmentConfig: boolean;
  updateSelfAssessmentConfigError: string | null;
  questionsAndResponses: AssessmentQuestionsAndResponses | null;
  isLoadingQuestionsAndResponses: boolean;
  questionsAndResponsesError: string | null;
  levelMix: LevelMixResponse | null;
  isLoadingLevelMix: boolean;
  levelMixError: string | null;
  accountUsers: any[];
  isLoadingAccountUsers: boolean;
  accountUsersError: string | null;
  accountTeamsBatch: Record<string, any>;
  isLoadingAccountTeams: boolean;
  accountTeamsError: string | null;
  disconnectReview: DisconnectReviewResponse | null;
  isLoadingDisconnectReview: boolean;
  disconnectReviewError: string | null;
  isTriggeringDisconnect: boolean;
  triggerDisconnectError: string | null;
  disconnectResolutions: DisconnectResolution[];
  disconnectResolutionsTotal: number;
  isLoadingDisconnectResolutions: boolean;
  disconnectResolutionsError: string | null;
  isRecordingResolution: boolean;
  recordResolutionError: string | null;
  isBulkUpdatingAssessmentStatus: boolean;
  isOverridingAccountLevel: boolean;
  isPublishingPostResolution: boolean;
  isPublishingAssessmentAccounts: boolean;
}

const initialState: AssessmentState = {
  templates: null,
  selectedTemplate: null,
  assessments: [],
  assessmentPagination: null,
  isLoading: false,
  isCreatingTemplate: false,
  isUpdatingTemplate: false,
  isCreatingAssessment: false,
  isUpdatingAssessment: false,
  isDeletingAssessment: false,
  isLoadingAssessments: false,
  error: null,
  createTemplateSuccess: false,
  createAssessmentSuccess: false,
  questionsAndResponses: null,
  isLoadingQuestionsAndResponses: false,
  questionsAndResponsesError: null,
  levelMix: null,
  isLoadingLevelMix: false,
  levelMixError: null,
  accountUsers: [],
  isLoadingAccountUsers: false,
  accountUsersError: null,
  accountTeamsBatch: {},
  isLoadingAccountTeams: false,
  accountTeamsError: null,
  disconnectReview: null,
  isLoadingDisconnectReview: false,
  disconnectReviewError: null,
  isTriggeringDisconnect: false,
  triggerDisconnectError: null,
  disconnectResolutions: [],
  disconnectResolutionsTotal: 0,
  isLoadingDisconnectResolutions: false,
  disconnectResolutionsError: null,
  isRecordingResolution: false,
  recordResolutionError: null,
  isBulkUpdatingAssessmentStatus: false,
  isOverridingAccountLevel: false,
  isPublishingPostResolution: false,
  isPublishingAssessmentAccounts: false,
  hubAssessmentsByStatus: {
    pending: [],
    active: [],
    completed: [],
    published: [],
  },
  hubPaginationByStatus: {
    pending: null,
    active: null,
    completed: null,
    published: null,
  },
  hubLoadingByStatus: {
    pending: false,
    active: false,
    completed: false,
    published: false,
  },
  hubErrorByStatus: {
    pending: null,
    active: null,
    completed: null,
    published: null,
  },
  hubTotalsByStatus: {
    pending: 0,
    active: 0,
    completed: 0,
    published: 0,
  },
  reviewAssessments: [],
  reviewAssessmentsLoading: false,
  reviewAssessmentsError: null,
  reviewAssessmentsMeta: null,
  isSubmittingReviewAssessment: false,
  submitReviewAssessmentError: null,
  submitReviewAssessmentSuccess: false,
  isCreatingSelfAssessment: false,
  createSelfAssessmentError: null,
  createdSelfAssessment: null,
  vendorSelfAssessments: [],
  vendorSelfAssessmentsTotal: 0,
  isLoadingVendorSelfAssessments: false,
  vendorSelfAssessmentsError: null,
  selfAssessmentLatest: null,
  isLoadingSelfAssessmentLatest: false,
  selfAssessmentLatestError: null,
  selfAssessmentConfig: null,
  isLoadingSelfAssessmentConfig: false,
  selfAssessmentConfigError: null,
  isUpdatingSelfAssessmentConfig: false,
  updateSelfAssessmentConfigError: null,
};

const extractTemplateFromResponse = (
  payload: unknown
): AssessmentTemplate | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const dataObject = payload as Record<string, any>;

  if (dataObject.id && dataObject.name) {
    return dataObject as AssessmentTemplate;
  }

  if (
    dataObject.template &&
    dataObject.template.id &&
    dataObject.template.name
  ) {
    return dataObject.template as AssessmentTemplate;
  }

  if (dataObject.data) {
    const nestedData = dataObject.data;
    if (
      nestedData.template &&
      nestedData.template.id &&
      nestedData.template.name
    ) {
      return nestedData.template as AssessmentTemplate;
    }
    if (nestedData.id && nestedData.name) {
      return nestedData as AssessmentTemplate;
    }
  }

  if (dataObject.result && dataObject.result.id && dataObject.result.name) {
    return dataObject.result as AssessmentTemplate;
  }

  return null;
};

// Async thunk to fetch assessment templates
export const fetchAssessmentTemplates = createAsyncThunk(
  "assessment/fetchTemplates",
  async (params: FetchTemplatesParams = {}, { rejectWithValue }) => {
    try {
      // Build query params, only include non-empty values
      const queryParams: Record<string, any> = {};
      if (params?.page) {
        queryParams.page = params.page;
      }
      if (params?.limit) {
        queryParams.limit = params.limit;
      }
      if (params?.search) {
        queryParams.search = params.search;
      }
      if (params?.questionnaire_type) {
        queryParams.questionnaire_type = params.questionnaire_type;
      }
      if (params?.category) {
        queryParams.category = params.category;
      }
      if (params?.sort_by) {
        queryParams.sort_by = params.sort_by;
      }
      if (params?.sort_order) {
        queryParams.sort_order = params.sort_order;
      }

      const url = API_URLS.getAssessmentTemplates(queryParams);
      const response = await get(url);
      return response as TemplateListResponse;
    } catch (error: any) {
      // Handle different error response formats
      let errorMessage = "Failed to fetch assessment templates";

      // Log the full error for debugging
      console.error("Error fetching assessment templates:", {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        url: error?.config?.url,
        message: error?.message,
      });

      if (error?.response?.data) {
        // Try different error response formats
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        // Check if error.message is a JSON string
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }

      // Include URL in error message if available
      if (error?.config?.url) {
        errorMessage += ` (URL: ${error.config.url})`;
      }

      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to create assessment template
export const createAssessmentTemplate = createAsyncThunk(
  "assessment/createTemplate",
  async (
    payload:
      | CreateTemplateRequest
      | { data: CreateTemplateRequest; queryParams?: Record<string, any> },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const queryParams =
        "queryParams" in payload ? payload.queryParams : undefined;

      const response = await post(
        API_URLS.createAssessmentTemplate(queryParams),
        payload
      );
      return response as AssessmentTemplate;
    } catch (error: any) {
      let errorMessage = "Failed to create assessment template";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data.errors || error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to update assessment template
export const updateAssessmentTemplate = createAsyncThunk(
  "assessment/updateTemplate",
  async (
    { id, data }: { id: string; data: CreateTemplateRequest },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const response = await put(API_URLS.updateAssessmentTemplate(id), data);
      return response as AssessmentTemplate;
    } catch (error: any) {
      let errorMessage = "Failed to update assessment template";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data.errors || error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to fetch assessments
export const fetchAssessments = createAsyncThunk(
  "assessment/fetchAssessments",
  async (params: FetchAssessmentsParams = {}, { rejectWithValue }) => {
    try {
      const queryParams: Record<string, any> = {};
      if (params?.page) {
        queryParams.page = params.page;
      }
      if (params?.limit) {
        queryParams.page_size = params.limit; // Backend uses page_size
      }
      if (params?.search) {
        queryParams.search = params.search;
      }
      if (params?.status && params.status !== "all-status") {
        // Backend expects uppercase status
        queryParams.status = params.status.toUpperCase();
      }
      if (params?.template_id) {
        queryParams.template_id = params.template_id;
      }
      if (params?.sort_by) {
        queryParams.sort_by = params.sort_by;
      }
      if (params?.sort_order) {
        queryParams.sort_order = params.sort_order;
      }

      const url = API_URLS.getAssessments(queryParams);
      const response = (await get(url)) as BackendAssessmentResponse;
      return response;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to fetch assessments");
    }
  }
);

// Async thunk to create assessment
export interface CreateAssessmentRejection {
  message: string;
  code?: string;
  accounts?: Array<{
    account_id: string;
    self_assessment_id?: string;
    formal_assessment_id?: string;
    submitted?: number;
    pending?: number;
  }>;
}

export const createAssessment = createAsyncThunk<
  Assessment,
  CreateAssessmentRequest,
  { rejectValue: CreateAssessmentRejection }
>(
  "assessment/createAssessment",
  async (data: CreateAssessmentRequest, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.createAssessment(), data);
      return response as Assessment;
    } catch (error: any) {
      let errorMessage = "Failed to create assessment";
      let code: string | undefined;
      let accounts: CreateAssessmentRejection["accounts"];
      // apiRequest() always throws a plain Error whose .message is a JSON
      // string of {status, message, code, content} — never a real axios
      // error with .response intact (see Services/ApiRequest.js).
      if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message || parsedError.content?.detail || error.message;
          code = parsedError.code ?? parsedError.content?.code;
          accounts = parsedError.content?.accounts;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue({ message: errorMessage, code, accounts });
    }
  }
);

// Async thunk to update assessment
export const updateAssessment = createAsyncThunk<
  AssessmentHubData,
  { id: string; data: UpdateAssessmentPayload },
  { rejectValue: string }
>("assessment/updateAssessment", async ({ id, data }, { rejectWithValue }) => {
  try {
    const response: any = await put(API_URLS.updateAssessment(id), data);
    const payload = response?.data ?? response;

    // Some environments return only { success, message } for updates.
    // Return a minimal success object so callers can use the message.
    if (!payload) {
      throw new Error("Invalid update assessment response");
    }
    if (!payload.id && !payload.assessment_id) {
      return {
        id,
        assessment_id: id,
        status: "draft",
        message: payload.message || "Assessment updated successfully",
      } as any;
    }

    return payload;
  } catch (error: any) {
    let errorMessage = "Failed to update assessment";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.detail ||
        error.response.data.message ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data.errors || error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

export const bulkUpdateAssessmentStatus = createAsyncThunk<
  { updated_count: number; failed_count: number },
  {
    assessment_ids: string[];
    status:
      | "active"
      | "completed"
      | "published"
      | "aborted"
      | "draft"
      | "pre_publish";
    reason?: string;
  },
  { rejectValue: string }
>(
  "assessment/bulkUpdateAssessmentStatus",
  async (payload, { rejectWithValue }) => {
    try {
      const response: any = await put(
        API_URLS.bulkUpdateAssessmentStatus(),
        payload
      );

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to update assessment status"
        );
      }

      const content = response?.content ?? response;
      return {
        updated_count: content?.updated_count ?? payload.assessment_ids.length,
        failed_count: content?.failed_count ?? 0,
      };
    } catch (error: any) {
      let errorMessage = "Failed to update assessment status";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export type FormalRunningAccount = {
  account_id: string;
  formal_assessment_id?: string;
};

export const checkFormalRunning = createAsyncThunk<
  FormalRunningAccount[],
  { account_ids: string[]; exclude_assessment_id?: string },
  { rejectValue: string }
>("assessment/checkFormalRunning", async (payload, { rejectWithValue }) => {
  try {
    const response: any = await post(API_URLS.checkFormalRunning(), {
      account_ids: payload.account_ids,
      ...(payload.exclude_assessment_id
        ? { exclude_assessment_id: payload.exclude_assessment_id }
        : {}),
    });

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message ||
          String((response.content as { detail?: string })?.detail ?? "") ||
          "Failed to check formal-running accounts"
      );
    }

    const accounts = response?.content?.accounts ?? response?.accounts ?? [];
    return Array.isArray(accounts) ? accounts : [];
  } catch (error: any) {
    let errorMessage = "Failed to check formal-running accounts";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.detail ||
        error.response.data.message ||
        errorMessage;
    } else if (error?.message) {
      errorMessage = error.message;
    }
    return rejectWithValue(errorMessage);
  }
});

export const mergeSelfAssignments = createAsyncThunk<
  unknown,
  {
    assessmentId: string;
    accountId: string;
    assignmentIds: string[];
  },
  { rejectValue: string }
>(
  "assessment/mergeSelfAssignments",
  async ({ assessmentId, accountId, assignmentIds }, { rejectWithValue }) => {
    try {
      const response: any = await post(
        API_URLS.mergeSelfAssignments(assessmentId, accountId),
        { assignment_ids: assignmentIds }
      );

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to merge team assessment"
        );
      }

      return response?.content ?? response;
    } catch (error: any) {
      let errorMessage = "Failed to merge team assessment";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          errorMessage;
      } else if (error?.message) {
        try {
          const parsed = JSON.parse(error.message);
          errorMessage = parsed.message || errorMessage;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to update assessment status
export const updateAssessmentStatus = createAsyncThunk<
  AssessmentHubData,
  {
    id: string;
    status: "active" | "completed" | "published" | "aborted";
    reason?: string;
  },
  { rejectValue: string }
>(
  "assessment/updateAssessmentStatus",
  async ({ id, status, reason }, { rejectWithValue }) => {
    try {
      const payload: any = { status };
      if (reason) {
        payload.reason = reason;
      }
      const response: any = await put(
        API_URLS.updateAssessmentStatus(id),
        payload
      );
      const responsePayload = response?.data ?? response;

      if (!responsePayload) {
        throw new Error("Invalid update assessment status response");
      }
      if (!responsePayload.id && !responsePayload.assessment_id) {
        return {
          id,
          assessment_id: id,
          status,
          message:
            responsePayload.message || "Assessment status updated successfully",
        } as any;
      }

      return responsePayload;
    } catch (error: any) {
      let errorMessage = "Failed to update assessment status";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data.errors || error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message || parsedError.detail || errorMessage;
        } catch {
          errorMessage = error.message || errorMessage;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to override account level
export const overrideAccountLevel = createAsyncThunk<
  { success: boolean },
  {
    assessmentId: string;
    accountId: string;
    overrideLevel: string;
    rationale: string;
    publishAccount?: boolean;
  },
  { rejectValue: string }
>(
  "assessment/overrideAccountLevel",
  async (
    { assessmentId, accountId, overrideLevel, rationale, publishAccount },
    { rejectWithValue }
  ) => {
    try {
      await post(API_URLS.overrideAccountLevel(assessmentId, accountId), {
        override_level: overrideLevel,
        rationale,
        ...(publishAccount ? { publish_account: true } : {}),
      });
      return { success: true };
    } catch (error: any) {
      let errorMessage = "Failed to override account level";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.detail ||
          error.response.data.message ||
          error.response.data.error?.message ||
          "Failed to override account level";
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            errorMessage;
        } catch {
          errorMessage = error.message || errorMessage;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to delete assessment
export const deleteAssessment = createAsyncThunk<
  { id: string },
  string,
  { rejectValue: string }
>("assessment/deleteAssessment", async (id, { rejectWithValue }) => {
  try {
    await del(API_URLS.deleteAssessment(id));
    return { id };
  } catch (error: any) {
    let errorMessage = "Failed to delete assessment";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.detail ||
        error.response.data.message ||
        error.response.data.error?.message ||
        "Failed to delete assessment";
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

// Async thunk to fetch review assessments
export const fetchReviewAssessments = createAsyncThunk<
  ReviewAssessmentsResponse,
  FetchReviewAssessmentsParams,
  { rejectValue: string }
>("assessment/fetchReviewAssessments", async (params, { rejectWithValue }) => {
  try {
    return (await get(
      API_URLS.getReviewAssessments({
        executive_id: params.executive_id,
        account_id: params.account_id,
        search: params.search,
        ...(params.tab && { tab: params.tab }),
        ...(params.is_draft && { is_draft: true }),
      })
    )) as ReviewAssessmentsResponse;
  } catch (error: any) {
    let errorMessage = "Failed to fetch review assessments";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

// Vendor-admin hub: list self-assessments (GET /assessments/self-assessments)
export const fetchVendorSelfAssessments = createAsyncThunk<
  BackendAssessmentResponse,
  FetchVendorSelfAssessmentsParams | undefined,
  { rejectValue: string }
>(
  "assessment/fetchVendorSelfAssessments",
  async (params, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.listVendorSelfAssessments({
          status: params?.status,
          search: params?.search,
          sort_by: params?.sort_by ?? "created_at",
          sort_order: params?.sort_order ?? "desc",
          ...(typeof params?.page === "number" ? { page: params.page } : {}),
          ...(typeof params?.page_size === "number"
            ? { page_size: params.page_size }
            : {}),
        })
      )) as {
        status?: string;
        message?: string;
        content?: BackendAssessmentResponse;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to fetch vendor team assessments"
        );
      }

      const payload = response?.content ?? response;
      return payload as BackendAssessmentResponse;
    } catch (error: any) {
      let errorMessage = "Failed to fetch vendor team assessments";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// GET /assessments/disconnect-review — sponsor level disconnects for Assessment Hub
export const fetchDisconnectReview = createAsyncThunk<
  DisconnectReviewResponse,
  FetchDisconnectReviewParams | undefined,
  { rejectValue: string }
>("assessment/fetchDisconnectReview", async (params, { rejectWithValue }) => {
  try {
    const response = (await get(
      API_URLS.getDisconnectReview({
        assessmentId: params?.assessmentId,
        assessmentStatus: params?.assessmentStatus ?? "all",
      })
    )) as {
      status?: string;
      message?: string;
      content?: DisconnectReviewResponse;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message ||
          String((response.content as { detail?: string })?.detail ?? "") ||
          "Failed to fetch disconnect review"
      );
    }

    const payload = response?.content ?? response;
    return payload as DisconnectReviewResponse;
  } catch (error: any) {
    let errorMessage = "Failed to fetch disconnect review";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

export const triggerDisconnectWorkflow = createAsyncThunk<
  TriggerDisconnectResponse,
  TriggerDisconnectPayload,
  { rejectValue: string }
>(
  "assessment/triggerDisconnectWorkflow",
  async (payload, { rejectWithValue }) => {
    try {
      const response = (await post(API_URLS.triggerDisconnect(), payload)) as {
        status?: string;
        message?: string;
        content?: TriggerDisconnectResponse;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to trigger disconnect workflow"
        );
      }

      const result = response?.content ?? response;
      return result as TriggerDisconnectResponse;
    } catch (error: any) {
      let errorMessage = "Failed to trigger disconnect workflow";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchDisconnectResolutions = createAsyncThunk<
  DisconnectResolutionsResponse,
  FetchDisconnectResolutionsParams | undefined,
  { rejectValue: string }
>(
  "assessment/fetchDisconnectResolutions",
  async (params, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.getDisconnectResolutions({
          status: params?.status,
          assessmentId: params?.assessmentId,
          accountId: params?.accountId,
          accountName: params?.accountName,
        })
      )) as {
        status?: string;
        message?: string;
        content?: DisconnectResolutionsResponse;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to fetch disconnect resolutions"
        );
      }

      const payload = response?.content ?? response;
      return {
        resolutions:
          (payload as DisconnectResolutionsResponse).resolutions ?? [],
        total: (payload as DisconnectResolutionsResponse).total ?? 0,
      };
    } catch (error: any) {
      let errorMessage = "Failed to fetch disconnect resolutions";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const recordDisconnectResolution = createAsyncThunk<
  { message?: string },
  RecordResolutionPayload,
  { rejectValue: string }
>(
  "assessment/recordDisconnectResolution",
  async (payload, { rejectWithValue }) => {
    try {
      const response = (await post(API_URLS.recordResolution(), payload)) as {
        status?: string;
        message?: string;
        content?: { message?: string; detail?: string };
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String(response.content?.detail ?? "") ||
            "Failed to record resolution"
        );
      }

      return {
        message:
          response.message ||
          response.content?.message ||
          "Resolution recorded successfully",
      };
    } catch (error: any) {
      let errorMessage = "Failed to record resolution";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

export const publishPostResolution = createAsyncThunk<
  { message?: string },
  PublishPostResolutionPayload,
  { rejectValue: string }
>("assessment/publishPostResolution", async (payload, { rejectWithValue }) => {
  try {
    const response = (await post(
      API_URLS.publishPostResolution(),
      payload
    )) as {
      status?: string;
      message?: string;
      content?: { message?: string; detail?: string };
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message ||
          String(response.content?.detail ?? "") ||
          "Failed to accept resolution"
      );
    }

    return {
      message:
        response.message ||
        response.content?.message ||
        "Resolution accepted successfully",
    };
  } catch (error: any) {
    let errorMessage = "Failed to accept resolution";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

export const publishAssessmentAccounts = createAsyncThunk<
  { message?: string },
  PublishAssessmentAccountsPayload,
  { rejectValue: string }
>(
  "assessment/publishAssessmentAccounts",
  async (payload, { rejectWithValue }) => {
    try {
      const body: Record<string, unknown> = {};
      if (payload.accountIds?.length) {
        body.accountIds = payload.accountIds;
      }
      if (payload.appendFromOngoing) {
        body.appendFromOngoing = true;
      }
      if (payload.force) {
        body.force = true;
      }

      const response = (await put(
        API_URLS.publishAssessmentAccounts(payload.assessmentId),
        body
      )) as {
        status?: string;
        message?: string;
        content?: { message?: string; detail?: string };
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String(response.content?.detail ?? "") ||
            "Failed to publish assessment"
        );
      }

      return {
        message: response.message || "Assessment published successfully",
      };
    } catch (error: any) {
      let errorMessage = "Failed to publish assessment";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to list self-assessments (GET /assessments/self)
export const fetchSelfAssessments = createAsyncThunk<
  ReviewAssessmentsResponse,
  FetchSelfAssessmentsParams | undefined,
  { rejectValue: string }
>("assessment/fetchSelfAssessments", async (params, { rejectWithValue }) => {
  try {
    const response = (await get(
      API_URLS.listSelfAssessments({
        status: params?.status ?? "draft",
        ...(params?.account_id ? { account_id: params.account_id } : {}),
        ...(params?.search ? { search: params.search } : {}),
        ...(typeof params?.created_by_me === "boolean"
          ? { created_by_me: params.created_by_me }
          : {}),
        ...(typeof params?.page === "number" ? { page: params.page } : {}),
        ...(typeof params?.page_size === "number"
          ? { page_size: params.page_size }
          : {}),
      })
    )) as {
      status?: string;
      message?: string;
      content?: ReviewAssessmentsResponse;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message ||
          String((response.content as { detail?: string })?.detail ?? "") ||
          "Failed to fetch team assessments"
      );
    }

    const payload = response?.content ?? response;
    return payload as ReviewAssessmentsResponse;
  } catch (error: any) {
    let errorMessage = "Failed to fetch team assessments";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

// GET /assessments/self/latest?account_id= — prior self-assessment for carry-forward / re-assess
export const fetchSelfAssessmentLatest = createAsyncThunk<
  LatestSelfAssessmentContent,
  string,
  { rejectValue: string }
>(
  "assessment/fetchSelfAssessmentLatest",
  async (accountId, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.getSelfAssessmentLatest(accountId)
      )) as {
        status?: string;
        message?: string;
        content?: LatestSelfAssessmentContent;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message ||
            String((response.content as { detail?: string })?.detail ?? "") ||
            "Failed to fetch latest team assessment"
        );
      }

      const payload = response?.content ?? response;
      return payload as LatestSelfAssessmentContent;
    } catch (error: any) {
      let errorMessage = "Failed to fetch latest team assessment";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// GET /self-assessment-config — org currency window for self-assessments
export const fetchSelfAssessmentConfig = createAsyncThunk<
  SelfAssessmentConfig,
  void,
  { rejectValue: string }
>("assessment/fetchSelfAssessmentConfig", async (_, { rejectWithValue }) => {
  try {
    const response = (await get(API_URLS.getSelfAssessmentConfig())) as {
      status?: string;
      message?: string;
      content?: SelfAssessmentConfig;
    };

    if (response?.status === "failure" || response?.status === "unauthorized") {
      return rejectWithValue(
        response.message || "Failed to fetch team assessment config"
      );
    }

    const payload = response?.content ?? response;
    const config = payload as SelfAssessmentConfig;
    if (!config?.id) {
      return rejectWithValue("Team assessment config is missing an id");
    }
    return config;
  } catch (error: any) {
    let errorMessage = "Failed to fetch team assessment config";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      errorMessage = error.message;
    }
    return rejectWithValue(errorMessage);
  }
});

// PUT /self-assessment-config/{id} — update AL assessment settings
export const updateSelfAssessmentConfig = createAsyncThunk<
  SelfAssessmentConfig,
  UpdateSelfAssessmentConfigPayload,
  { rejectValue: string }
>(
  "assessment/updateSelfAssessmentConfig",
  async (
    {
      id,
      days,
      account_days,
      formal_due_reminder_days,
      team_due_reminder_days,
      cadence_days,
      window_days,
      window_offset_days,
      resolution_days,
      grace_extra_days,
    },
    { rejectWithValue }
  ) => {
    try {
      const response = (await put(API_URLS.updateSelfAssessmentConfig(id), {
        ...(days != null ? { days } : {}),
        ...(account_days != null ? { account_days } : {}),
        ...(formal_due_reminder_days != null
          ? { formal_due_reminder_days }
          : {}),
        ...(team_due_reminder_days != null ? { team_due_reminder_days } : {}),
        ...(cadence_days != null ? { cadence_days } : {}),
        ...(window_days != null ? { window_days } : {}),
        ...(window_offset_days === null
          ? { clear_window_offset: true }
          : window_offset_days != null
            ? { window_offset_days }
            : {}),
        ...(resolution_days != null ? { resolution_days } : {}),
        ...(grace_extra_days != null ? { grace_extra_days } : {}),
      })) as {
        status?: string;
        message?: string;
        content?: SelfAssessmentConfig;
      };

      if (
        response?.status === "failure" ||
        response?.status === "unauthorized"
      ) {
        return rejectWithValue(
          response.message || "Failed to update team assessment config"
        );
      }

      const payload = response?.content ?? response;
      const config = payload as SelfAssessmentConfig;
      if (!config?.id) {
        return { id, days, ...(config as object) } as SelfAssessmentConfig;
      }
      return config;
    } catch (error: any) {
      let errorMessage = "Failed to update team assessment config";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to create a self-assessment (POST /assessments/self)
export type CreateSelfAssessmentRejection = {
  message: string;
  code?: string;
  assessment_id?: string;
};

export const createSelfAssessment = createAsyncThunk<
  Record<string, unknown>,
  CreateSelfAssessmentPayload,
  { rejectValue: CreateSelfAssessmentRejection }
>("assessment/createSelfAssessment", async (payload, { rejectWithValue }) => {
  try {
    const response = (await post(API_URLS.createSelfAssessment(), payload)) as {
      status?: string;
      message?: string;
      content?: Record<string, unknown>;
      response?: { status?: number; message?: string };
    };

    if (
      response?.status === "failure" ||
      response?.status === "unauthorized" ||
      response?.status === "conflict" ||
      response?.response?.status === 400
    ) {
      const content = response?.content ?? {};
      return rejectWithValue({
        message:
          response?.message ||
          response?.response?.message ||
          "Failed to create team assessment",
        code: (content.code as string | undefined) ?? response?.status,
        assessment_id: content.assessment_id as string | undefined,
      });
    }

    const item = response?.content ?? response;
    return (item ?? {}) as Record<string, unknown>;
  } catch (error: unknown) {
    const err = error as {
      response?: { data?: { response?: { message?: string } } };
      message?: string;
    };
    const directMessage = err?.response?.data?.response?.message;
    if (directMessage) {
      return rejectWithValue({ message: directMessage });
    }

    try {
      const parsed = JSON.parse(err?.message || "") as {
        message?: string;
        code?: string;
        content?: { code?: string; assessment_id?: string; detail?: string };
      };
      return rejectWithValue({
        message:
          parsed.message ||
          parsed.content?.detail ||
          "Failed to create team assessment",
        code: parsed.code ?? parsed.content?.code,
        assessment_id: parsed.content?.assessment_id,
      });
    } catch {
      // apiRequest error message is not JSON
    }

    return rejectWithValue({ message: "Failed to create team assessment" });
  }
});

// Async thunk to submit review assessment
export const submitReviewAssessment = createAsyncThunk<
  SubmitReviewAssessmentResponse,
  SubmitReviewAssessmentPayload,
  { rejectValue: string }
>("assessment/submitReviewAssessment", async (payload, { rejectWithValue }) => {
  try {
    const response = (await post(
      API_URLS.submitReviewAssessment(),
      payload
    )) as SubmitReviewAssessmentResponse;

    return response;
  } catch (error: any) {
    let errorMessage = "Failed to submit review assessment";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

// Async thunk to fetch level mix
export const fetchLevelMix = createAsyncThunk<
  LevelMixResponse,
  FetchLevelMixParams,
  { rejectValue: string }
>("assessment/fetchLevelMix", async (params, { rejectWithValue }) => {
  try {
    const response = (await get(
      API_URLS.getLevelMix(params)
    )) as LevelMixResponse;

    return response;
  } catch (error: any) {
    let errorMessage = "Failed to fetch level mix data";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message || parsedError.detail || error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

const getThunkErrorMessage = (error: any, fallback: string): string => {
  if (error?.response?.data) {
    const data = error.response.data;
    return data.message || data.detail || data.error?.message || fallback;
  }
  if (error?.message) {
    try {
      const parsedError = JSON.parse(error.message);
      return parsedError.message || parsedError.detail || error.message;
    } catch {
      return error.message;
    }
  }
  return fallback;
};

// Stateless variants below return the raw response and have no extraReducers,
// so callers can fetch per-account / per-assessment data without clobbering
// the shared list state (reviewAssessments, selfAssessments, levelMix).

// Trend snapshots for LevelMix: runs in parallel, must not touch levelMix state
export const fetchLevelMixSnapshot = createAsyncThunk<
  unknown,
  Record<string, string>,
  { rejectValue: string }
>("assessment/fetchLevelMixSnapshot", async (params, { rejectWithValue }) => {
  try {
    return await get(API_URLS.getLevelMix(params));
  } catch (error: any) {
    return rejectWithValue(
      getThunkErrorMessage(error, "Failed to fetch level mix data")
    );
  }
});

export const fetchAssessmentValidation = createAsyncThunk<
  unknown,
  string,
  { rejectValue: string }
>(
  "assessment/fetchAssessmentValidation",
  async (accountId, { rejectWithValue }) => {
    try {
      return await get(API_URLS.getAssessmentValidation(accountId));
    } catch (error: any) {
      return rejectWithValue(
        getThunkErrorMessage(error, "Failed to validate assessment eligibility")
      );
    }
  }
);

export const fetchAssessmentCalendarStatus = createAsyncThunk<
  unknown,
  void,
  { rejectValue: string }
>(
  "assessment/fetchAssessmentCalendarStatus",
  async (_, { rejectWithValue }) => {
    try {
      return await get(API_URLS.getAssessmentCalendarStatus());
    } catch (error: any) {
      return rejectWithValue(
        getThunkErrorMessage(error, "Failed to fetch assessment calendar")
      );
    }
  }
);

export const lookupReviewAssessments = createAsyncThunk<
  unknown,
  FetchReviewAssessmentsParams,
  { rejectValue: string }
>("assessment/lookupReviewAssessments", async (params, { rejectWithValue }) => {
  try {
    return await get(API_URLS.getReviewAssessments(params));
  } catch (error: any) {
    return rejectWithValue(
      getThunkErrorMessage(error, "Failed to fetch review assessments")
    );
  }
});

export const lookupSelfAssessments = createAsyncThunk<
  unknown,
  FetchSelfAssessmentsParams,
  { rejectValue: string }
>("assessment/lookupSelfAssessments", async (params, { rejectWithValue }) => {
  try {
    return await get(API_URLS.listSelfAssessments(params));
  } catch (error: any) {
    return rejectWithValue(
      getThunkErrorMessage(error, "Failed to fetch team assessments")
    );
  }
});

export const fetchAccountAssessmentResponses = createAsyncThunk<
  unknown,
  { assessmentId: string; accountId: string },
  { rejectValue: string }
>(
  "assessment/fetchAccountAssessmentResponses",
  async ({ assessmentId, accountId }, { rejectWithValue }) => {
    try {
      return await get(
        API_URLS.getAssessmentResponses(assessmentId, {
          account_id: accountId,
          limit: 100,
          is_draft: false,
        })
      );
    } catch (error: any) {
      return rejectWithValue(
        getThunkErrorMessage(error, "Failed to fetch assessment responses")
      );
    }
  }
);

// Async thunk to fetch assessment questions and responses
export const fetchAssessmentQuestionsAndResponses = createAsyncThunk<
  AssessmentQuestionsAndResponses,
  string,
  { rejectValue: string }
>(
  "assessment/fetchAssessmentQuestionsAndResponses",
  async (assessmentId, { rejectWithValue }) => {
    try {
      const response = (await get(
        API_URLS.getAssessmentQuestionsAndResponses(assessmentId)
      )) as AssessmentQuestionsAndResponses;

      return response;
    } catch (error: any) {
      let errorMessage = "Failed to fetch questions and responses";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          JSON.stringify(error.response.data);
      } else if (error?.message) {
        try {
          const parsedError = JSON.parse(error.message);
          errorMessage =
            parsedError.message ||
            parsedError.detail ||
            parsedError.error?.message ||
            error.message;
        } catch {
          errorMessage = error.message;
        }
      }
      return rejectWithValue(errorMessage);
    }
  }
);

// Async thunk to fetch account users
export const fetchAccountUsers = createAsyncThunk<
  any[],
  Record<string, any> | undefined,
  { rejectValue: string }
>("assessment/fetchAccountUsers", async (params = {}, { rejectWithValue }) => {
  try {
    const response = await get(API_URLS.getAccountUsers(params));

    // Handle response structure: { total: number, items: [...] }
    const accountsData =
      response?.items ||
      (Array.isArray(response) ? response : []) ||
      response?.data ||
      response?.accounts ||
      [];

    return accountsData;
  } catch (error: any) {
    let errorMessage = "Failed to fetch account users";
    if (error?.response?.data) {
      errorMessage =
        error.response.data.message ||
        error.response.data.detail ||
        error.response.data.error?.message ||
        JSON.stringify(error.response.data);
    } else if (error?.message) {
      try {
        const parsedError = JSON.parse(error.message);
        errorMessage =
          parsedError.message ||
          parsedError.detail ||
          parsedError.error?.message ||
          error.message;
      } catch {
        errorMessage = error.message;
      }
    }
    return rejectWithValue(errorMessage);
  }
});

export interface FetchAssessmentAccountsTeamsBatchParams {
  accountIds: string[];
}

export const fetchAssessmentAccountsTeamsBatch = createAsyncThunk<
  Record<string, any>,
  FetchAssessmentAccountsTeamsBatchParams,
  { rejectValue: string }
>(
  "assessment/fetchAssessmentAccountsTeamsBatch",
  async ({ accountIds }, { rejectWithValue }) => {
    try {
      const response = await post(API_URLS.getAssessmentAccountsTeamsBatch(), {
        accountIds,
      });
      return response?.content?.byAccountId ?? {};
    } catch (error: any) {
      let errorMessage = "Failed to load account stakeholders";
      if (error?.response?.data) {
        errorMessage =
          error.response.data.message ||
          error.response.data.detail ||
          error.response.data.error?.message ||
          errorMessage;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      return rejectWithValue(errorMessage);
    }
  }
);

const AssessmentSlice = createSlice({
  name: "assessment",
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearSelectedTemplate: state => {
      state.selectedTemplate = null;
    },
    clearCreateTemplateSuccess: state => {
      state.createTemplateSuccess = false;
    },
    clearCreateAssessmentSuccess: state => {
      state.createAssessmentSuccess = false;
    },
    clearSubmitReviewAssessmentSuccess: state => {
      state.submitReviewAssessmentSuccess = false;
      state.submitReviewAssessmentError = null;
    },
    clearAccountTeamsBatch: state => {
      state.accountTeamsBatch = {};
      state.isLoadingAccountTeams = false;
      state.accountTeamsError = null;
    },
    clearSelfAssessmentLatest: state => {
      state.selfAssessmentLatest = null;
      state.isLoadingSelfAssessmentLatest = false;
      state.selfAssessmentLatestError = null;
    },
  },
  extraReducers: builder => {
    // Fetch templates
    builder
      .addCase(fetchAssessmentTemplates.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAssessmentTemplates.fulfilled, (state, action) => {
        state.isLoading = false;

        if (action.payload) {
          state.templates = action.payload;
          state.error = null;
        } else {
          state.error = "Unexpected response format from server";
          state.templates = null;
        }
      })
      .addCase(fetchAssessmentTemplates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "Failed to fetch templates";
        state.templates = null;
      });

    // Create template
    builder
      .addCase(createAssessmentTemplate.pending, state => {
        state.isCreatingTemplate = true;
        state.error = null;
        state.createTemplateSuccess = false;
      })
      .addCase(createAssessmentTemplate.fulfilled, state => {
        state.isCreatingTemplate = false;
        // templates list already updated by fetchAssessmentTemplates in thunk
        state.createTemplateSuccess = true;
        state.error = null;
      })
      .addCase(createAssessmentTemplate.rejected, (state, action) => {
        state.isCreatingTemplate = false;
        state.error = (action.payload as string) || "Failed to create template";
        state.createTemplateSuccess = false;
      });

    // Update template
    builder
      .addCase(updateAssessmentTemplate.pending, state => {
        state.isUpdatingTemplate = true;
        state.error = null;
      })
      .addCase(updateAssessmentTemplate.fulfilled, (state, action) => {
        state.isUpdatingTemplate = false;
        // templates list already updated by fetchAssessmentTemplates in thunk
        const updatedTemplate = extractTemplateFromResponse(action.payload);
        if (
          updatedTemplate?.id &&
          state.selectedTemplate?.id === updatedTemplate.id
        ) {
          state.selectedTemplate = updatedTemplate;
        }
        state.error = null;
      })
      .addCase(updateAssessmentTemplate.rejected, (state, action) => {
        state.isUpdatingTemplate = false;
        state.error = (action.payload as string) || "Failed to update template";
      });

    // Fetch assessments
    builder
      .addCase(fetchAssessments.pending, state => {
        state.isLoadingAssessments = true;
        state.error = null;
      })
      .addCase(fetchAssessments.fulfilled, (state, action) => {
        state.isLoadingAssessments = false;
        state.assessments = action.payload.assessments;
        state.error = null;
      })
      .addCase(fetchAssessments.rejected, (state, action) => {
        state.isLoadingAssessments = false;
        state.error =
          (action.payload as string) || "Failed to fetch assessments";
        state.assessments = [];
        state.assessmentPagination = null;
      });

    // Create assessment
    builder
      .addCase(createAssessment.pending, state => {
        state.isCreatingAssessment = true;
        state.error = null;
        state.createAssessmentSuccess = false;
      })
      .addCase(createAssessment.fulfilled, (state, action) => {
        state.isCreatingAssessment = false;
        state.createAssessmentSuccess = true;
        state.error = null;
      })
      .addCase(createAssessment.rejected, (state, action) => {
        state.isCreatingAssessment = false;
        state.error = action.payload?.message || "Failed to create assessment";
        state.createAssessmentSuccess = false;
      });

    // Update assessment
    builder
      .addCase(updateAssessment.pending, state => {
        state.isUpdatingAssessment = true;
        state.error = null;
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        state.isUpdatingAssessment = false;
        const updatedAssessment = action.payload;
        if (!updatedAssessment?.id || !updatedAssessment?.status) {
          state.error = null;
          return;
        }
        const newStatus = mapStatusToHubKey(updatedAssessment.status);

        HUB_STATUS_KEYS.forEach(statusKey => {
          state.hubAssessmentsByStatus[statusKey] =
            state.hubAssessmentsByStatus[statusKey].filter(
              item => item.id !== updatedAssessment.id
            );
        });

        state.hubAssessmentsByStatus[newStatus] = [
          updatedAssessment,
          ...state.hubAssessmentsByStatus[newStatus],
        ];
        state.error = null;
      })
      .addCase(updateAssessment.rejected, (state, action) => {
        state.isUpdatingAssessment = false;
        state.error =
          (action.payload as string) || "Failed to update assessment";
      });

    // Update assessment status
    builder
      .addCase(updateAssessmentStatus.pending, state => {
        state.isUpdatingAssessment = true;
        state.error = null;
      })
      .addCase(updateAssessmentStatus.fulfilled, (state, action) => {
        state.isUpdatingAssessment = false;
        const updatedAssessment = action.payload;
        if (!updatedAssessment?.id || !updatedAssessment?.status) {
          state.error = null;
          return;
        }
        const newStatus = mapStatusToHubKey(updatedAssessment.status);

        // Remove from old status arrays
        HUB_STATUS_KEYS.forEach(statusKey => {
          state.hubAssessmentsByStatus[statusKey] =
            state.hubAssessmentsByStatus[statusKey].filter(
              a => a.id !== updatedAssessment.id
            );
        });

        // Add to new status array
        state.hubAssessmentsByStatus[newStatus] = [
          updatedAssessment,
          ...state.hubAssessmentsByStatus[newStatus],
        ];
        state.error = null;
      })
      .addCase(updateAssessmentStatus.rejected, (state, action) => {
        state.isUpdatingAssessment = false;
        state.error =
          (action.payload as string) || "Failed to update assessment status";
      });

    // Delete assessment
    builder
      .addCase(deleteAssessment.pending, state => {
        state.isDeletingAssessment = true;
        state.error = null;
      })
      .addCase(deleteAssessment.fulfilled, (state, action) => {
        state.isDeletingAssessment = false;
        HUB_STATUS_KEYS.forEach(statusKey => {
          state.hubAssessmentsByStatus[statusKey] =
            state.hubAssessmentsByStatus[statusKey].filter(
              item => item.id !== action.payload.id
            );
        });
        state.error = null;
      })
      .addCase(deleteAssessment.rejected, (state, action) => {
        state.isDeletingAssessment = false;
        state.error =
          (action.payload as string) || "Failed to delete assessment";
      });

    // Fetch review assessments
    builder
      .addCase(fetchReviewAssessments.pending, state => {
        state.reviewAssessmentsLoading = true;
        state.reviewAssessmentsError = null;
      })
      .addCase(fetchReviewAssessments.fulfilled, (state, action) => {
        state.reviewAssessmentsLoading = false;
        state.reviewAssessments = action.payload.assessments;
        state.reviewAssessmentsMeta = action.payload.meta;
        state.reviewAssessmentsError = null;
      })
      .addCase(fetchReviewAssessments.rejected, (state, action) => {
        state.reviewAssessmentsLoading = false;
        state.reviewAssessmentsError =
          (action.payload as string) || "Failed to fetch review assessments";
        state.reviewAssessments = [];
        state.reviewAssessmentsMeta = null;
      });

    // Vendor self-assessments hub tab
    builder
      .addCase(fetchVendorSelfAssessments.pending, state => {
        state.isLoadingVendorSelfAssessments = true;
        state.vendorSelfAssessmentsError = null;
      })
      .addCase(fetchVendorSelfAssessments.fulfilled, (state, action) => {
        state.isLoadingVendorSelfAssessments = false;
        state.vendorSelfAssessments = action.payload.assessments ?? [];
        state.vendorSelfAssessmentsTotal = action.payload.total ?? 0;
        state.vendorSelfAssessmentsError = null;
      })
      .addCase(fetchVendorSelfAssessments.rejected, (state, action) => {
        state.isLoadingVendorSelfAssessments = false;
        state.vendorSelfAssessmentsError =
          (action.payload as string) ||
          "Failed to fetch vendor team assessments";
        state.vendorSelfAssessments = [];
        state.vendorSelfAssessmentsTotal = 0;
      });

    // Disconnect review (Assessment Hub — Disconnects tab)
    builder
      .addCase(fetchDisconnectReview.pending, state => {
        state.isLoadingDisconnectReview = true;
        state.disconnectReviewError = null;
      })
      .addCase(fetchDisconnectReview.fulfilled, (state, action) => {
        state.isLoadingDisconnectReview = false;
        state.disconnectReview = action.payload;
        state.disconnectReviewError = null;
      })
      .addCase(fetchDisconnectReview.rejected, (state, action) => {
        state.isLoadingDisconnectReview = false;
        state.disconnectReviewError =
          (action.payload as string) || "Failed to fetch disconnect review";
        state.disconnectReview = null;
      });

    builder
      .addCase(triggerDisconnectWorkflow.pending, state => {
        state.isTriggeringDisconnect = true;
        state.triggerDisconnectError = null;
      })
      .addCase(triggerDisconnectWorkflow.fulfilled, state => {
        state.isTriggeringDisconnect = false;
        state.triggerDisconnectError = null;
      })
      .addCase(triggerDisconnectWorkflow.rejected, (state, action) => {
        state.isTriggeringDisconnect = false;
        state.triggerDisconnectError =
          (action.payload as string) || "Failed to trigger disconnect workflow";
      });

    builder
      .addCase(fetchDisconnectResolutions.pending, state => {
        state.isLoadingDisconnectResolutions = true;
        state.disconnectResolutionsError = null;
      })
      .addCase(fetchDisconnectResolutions.fulfilled, (state, action) => {
        state.isLoadingDisconnectResolutions = false;
        state.disconnectResolutions = action.payload.resolutions ?? [];
        state.disconnectResolutionsTotal = action.payload.total ?? 0;
        state.disconnectResolutionsError = null;
      })
      .addCase(fetchDisconnectResolutions.rejected, (state, action) => {
        state.isLoadingDisconnectResolutions = false;
        state.disconnectResolutionsError =
          (action.payload as string) ||
          "Failed to fetch disconnect resolutions";
        state.disconnectResolutions = [];
        state.disconnectResolutionsTotal = 0;
      });

    builder
      .addCase(recordDisconnectResolution.pending, state => {
        state.isRecordingResolution = true;
        state.recordResolutionError = null;
      })
      .addCase(recordDisconnectResolution.fulfilled, state => {
        state.isRecordingResolution = false;
        state.recordResolutionError = null;
      })
      .addCase(recordDisconnectResolution.rejected, (state, action) => {
        state.isRecordingResolution = false;
        state.recordResolutionError =
          (action.payload as string) || "Failed to record resolution";
      });

    // Fetch self-assessments (draft tab)
    builder
      .addCase(fetchSelfAssessments.pending, state => {
        state.reviewAssessmentsLoading = true;
        state.reviewAssessmentsError = null;
      })
      .addCase(fetchSelfAssessments.fulfilled, (state, action) => {
        state.reviewAssessmentsLoading = false;
        state.reviewAssessments = action.payload.assessments;
        state.reviewAssessmentsMeta = action.payload.meta;
        state.reviewAssessmentsError = null;
      })
      .addCase(fetchSelfAssessments.rejected, (state, action) => {
        state.reviewAssessmentsLoading = false;
        state.reviewAssessmentsError =
          (action.payload as string) || "Failed to fetch team assessments";
        state.reviewAssessments = [];
        state.reviewAssessmentsMeta = null;
      });

    // Latest self-assessment for portfolio carry-forward / re-assess
    builder
      .addCase(fetchSelfAssessmentLatest.pending, state => {
        state.isLoadingSelfAssessmentLatest = true;
        state.selfAssessmentLatestError = null;
      })
      .addCase(fetchSelfAssessmentLatest.fulfilled, (state, action) => {
        state.isLoadingSelfAssessmentLatest = false;
        state.selfAssessmentLatest = action.payload;
        state.selfAssessmentLatestError = null;
      })
      .addCase(fetchSelfAssessmentLatest.rejected, (state, action) => {
        state.isLoadingSelfAssessmentLatest = false;
        state.selfAssessmentLatestError =
          (action.payload as string) ||
          "Failed to fetch latest team assessment";
        state.selfAssessmentLatest = null;
      });

    // Self-assessment org config (currency window)
    builder
      .addCase(fetchSelfAssessmentConfig.pending, state => {
        state.isLoadingSelfAssessmentConfig = true;
        state.selfAssessmentConfigError = null;
      })
      .addCase(fetchSelfAssessmentConfig.fulfilled, (state, action) => {
        state.isLoadingSelfAssessmentConfig = false;
        state.selfAssessmentConfig = action.payload;
        state.selfAssessmentConfigError = null;
      })
      .addCase(fetchSelfAssessmentConfig.rejected, (state, action) => {
        state.isLoadingSelfAssessmentConfig = false;
        state.selfAssessmentConfigError =
          (action.payload as string) ||
          "Failed to fetch team assessment config";
        state.selfAssessmentConfig = null;
      })
      .addCase(updateSelfAssessmentConfig.pending, state => {
        state.isUpdatingSelfAssessmentConfig = true;
        state.updateSelfAssessmentConfigError = null;
      })
      .addCase(updateSelfAssessmentConfig.fulfilled, (state, action) => {
        state.isUpdatingSelfAssessmentConfig = false;
        state.selfAssessmentConfig = action.payload;
        state.updateSelfAssessmentConfigError = null;
      })
      .addCase(updateSelfAssessmentConfig.rejected, (state, action) => {
        state.isUpdatingSelfAssessmentConfig = false;
        state.updateSelfAssessmentConfigError =
          (action.payload as string) ||
          "Failed to update team assessment config";
      });

    // Create self-assessment
    builder
      .addCase(createSelfAssessment.pending, state => {
        state.isCreatingSelfAssessment = true;
        state.createSelfAssessmentError = null;
      })
      .addCase(createSelfAssessment.fulfilled, (state, action) => {
        state.isCreatingSelfAssessment = false;
        state.createdSelfAssessment = action.payload;
        state.createSelfAssessmentError = null;
      })
      .addCase(createSelfAssessment.rejected, (state, action) => {
        state.isCreatingSelfAssessment = false;
        state.createSelfAssessmentError =
          action.payload?.message || "Failed to create team assessment";
        state.createdSelfAssessment = null;
      });

    // Submit review assessment
    builder
      .addCase(submitReviewAssessment.pending, state => {
        state.isSubmittingReviewAssessment = true;
        state.submitReviewAssessmentError = null;
        state.submitReviewAssessmentSuccess = false;
      })
      .addCase(submitReviewAssessment.fulfilled, (state, action) => {
        state.isSubmittingReviewAssessment = false;
        state.submitReviewAssessmentSuccess = true;
        state.submitReviewAssessmentError = null;
      })
      .addCase(submitReviewAssessment.rejected, (state, action) => {
        state.isSubmittingReviewAssessment = false;
        state.submitReviewAssessmentError =
          (action.payload as string) || "Failed to submit review assessment";
        state.submitReviewAssessmentSuccess = false;
      });

    // Fetch assessment questions and responses
    builder
      .addCase(fetchAssessmentQuestionsAndResponses.pending, state => {
        state.isLoadingQuestionsAndResponses = true;
        state.questionsAndResponsesError = null;
      })
      .addCase(
        fetchAssessmentQuestionsAndResponses.fulfilled,
        (state, action) => {
          state.isLoadingQuestionsAndResponses = false;
          state.questionsAndResponses = action.payload;
          state.questionsAndResponsesError = null;
        }
      )
      .addCase(
        fetchAssessmentQuestionsAndResponses.rejected,
        (state, action) => {
          state.isLoadingQuestionsAndResponses = false;
          state.questionsAndResponsesError =
            (action.payload as string) ||
            "Failed to fetch questions and responses";
          state.questionsAndResponses = null;
        }
      );

    // Fetch level mix
    builder
      .addCase(fetchLevelMix.pending, state => {
        state.isLoadingLevelMix = true;
        state.levelMixError = null;
      })
      .addCase(fetchLevelMix.fulfilled, (state, action) => {
        state.isLoadingLevelMix = false;
        state.levelMix = action.payload;
        state.levelMixError = null;
      })
      .addCase(fetchLevelMix.rejected, (state, action) => {
        state.isLoadingLevelMix = false;
        state.levelMixError =
          (action.payload as string) || "Failed to fetch level mix data";
        state.levelMix = null;
      });

    // Fetch account users
    builder
      .addCase(fetchAccountUsers.pending, state => {
        state.isLoadingAccountUsers = true;
        state.accountUsersError = null;
      })
      .addCase(fetchAccountUsers.fulfilled, (state, action) => {
        state.isLoadingAccountUsers = false;
        state.accountUsers = action.payload;
        state.accountUsersError = null;
      })
      .addCase(fetchAccountUsers.rejected, (state, action) => {
        state.isLoadingAccountUsers = false;
        state.accountUsersError =
          (action.payload as string) || "Failed to fetch account users";
        state.accountUsers = [];
      });

    // Fetch account teams batch (stakeholders for assessment targeting)
    builder
      .addCase(fetchAssessmentAccountsTeamsBatch.pending, state => {
        state.isLoadingAccountTeams = true;
        state.accountTeamsError = null;
      })
      .addCase(fetchAssessmentAccountsTeamsBatch.fulfilled, (state, action) => {
        state.isLoadingAccountTeams = false;
        state.accountTeamsBatch = action.payload;
        state.accountTeamsError = null;
      })
      .addCase(fetchAssessmentAccountsTeamsBatch.rejected, (state, action) => {
        state.isLoadingAccountTeams = false;
        state.accountTeamsError =
          (action.payload as string) || "Failed to load account stakeholders";
        state.accountTeamsBatch = {};
      });

    // Bulk / publish / override mutations (results consumed via unwrap + refetch)
    builder
      .addCase(bulkUpdateAssessmentStatus.pending, state => {
        state.isBulkUpdatingAssessmentStatus = true;
        state.error = null;
      })
      .addCase(bulkUpdateAssessmentStatus.fulfilled, state => {
        state.isBulkUpdatingAssessmentStatus = false;
        state.error = null;
      })
      .addCase(bulkUpdateAssessmentStatus.rejected, (state, action) => {
        state.isBulkUpdatingAssessmentStatus = false;
        state.error =
          (action.payload as string) || "Failed to update assessment status";
      })
      .addCase(overrideAccountLevel.pending, state => {
        state.isOverridingAccountLevel = true;
        state.error = null;
      })
      .addCase(overrideAccountLevel.fulfilled, state => {
        state.isOverridingAccountLevel = false;
        state.error = null;
      })
      .addCase(overrideAccountLevel.rejected, (state, action) => {
        state.isOverridingAccountLevel = false;
        state.error =
          (action.payload as string) || "Failed to override account level";
      })
      .addCase(publishPostResolution.pending, state => {
        state.isPublishingPostResolution = true;
        state.error = null;
      })
      .addCase(publishPostResolution.fulfilled, state => {
        state.isPublishingPostResolution = false;
        state.error = null;
      })
      .addCase(publishPostResolution.rejected, (state, action) => {
        state.isPublishingPostResolution = false;
        state.error =
          (action.payload as string) || "Failed to accept resolution";
      })
      .addCase(publishAssessmentAccounts.pending, state => {
        state.isPublishingAssessmentAccounts = true;
        state.error = null;
      })
      .addCase(publishAssessmentAccounts.fulfilled, state => {
        state.isPublishingAssessmentAccounts = false;
        state.error = null;
      })
      .addCase(publishAssessmentAccounts.rejected, (state, action) => {
        state.isPublishingAssessmentAccounts = false;
        state.error =
          (action.payload as string) || "Failed to publish assessment";
      });
  },
});

export const {
  clearError,
  clearSelectedTemplate,
  clearCreateTemplateSuccess,
  clearCreateAssessmentSuccess,
  clearSubmitReviewAssessmentSuccess,
  clearAccountTeamsBatch,
  clearSelfAssessmentLatest,
} = AssessmentSlice.actions;
export default AssessmentSlice.reducer;
